import React from "react";
import BehaviourBarGraph from "../BehaviourBarGraph.jsx";
import {
  formatMass,
  formatPercentage,
} from "@signolteam/shared/units-of-measure";

let defaultMetadata = {
  fuel_burn_reti_1_without_cooldown: 100,
  fuel_burn_reti_2_without_cooldown: 40,
  actual_taxi_in_engine1_fuel_burn_kg: 100,
  actual_taxi_in_engine2_fuel_burn_kg: 40,
  actual_taxi_in_duration_minutes: 8,
  constant_reti_cooldown_time: 3,
  constant_reti_threshold: 0.2,
};

export const behaviourMetadataFields = Object.keys(defaultMetadata);
export default function graph({
  bvrColour,
  unit = "kg",
  metadata = defaultMetadata,
  industry,
  missingDataMsg,
  isExample,
}) {
  let engine1Value = Math.max(
    metadata["fuel_burn_reti_1_without_cooldown"],
    metadata["actual_taxi_in_engine1_fuel_burn_kg"],
  );
  let engine2Value = Math.max(
    metadata["fuel_burn_reti_2_without_cooldown"],
    metadata["actual_taxi_in_engine2_fuel_burn_kg"],
  );
  let thresholdPercentage = 1 - Number(metadata["constant_reti_threshold"]);

  if (isNaN(engine1Value) || isNaN(engine2Value) || !thresholdPercentage) {
    return (
      <div className="relative text-center my-6 mx-auto text-2xl fill-current">
        {missingDataMsg}
      </div>
    );
  }

  let maxEngineValue =
    engine1Value > engine2Value ? engine1Value : engine2Value;
  let minEngineValue =
    engine1Value > engine2Value ? engine2Value : engine1Value;
  let thresholdValue = Number(maxEngineValue * thresholdPercentage);
  let successStatus = minEngineValue < thresholdValue;
  const primaryEngine = engine1Value >= engine2Value ? 1 : 2;

  const data = [
    {
      name: "Engine 1",
      mobileName: `Label 1`,
      beforeThreshold:
        engine1Value > thresholdValue ? thresholdValue : engine1Value,
      afterThreshold:
        engine1Value > thresholdValue ? engine1Value - thresholdValue : 0,
      underThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : bvrColour.codes["200"],
      overThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : primaryEngine === 1
          ? bvrColour.codes["200"]
          : bvrColour.codes["900"],
    },
    {
      name: "Engine 2",
      mobileName: `Label 2`,
      beforeThreshold:
        engine2Value > thresholdValue ? thresholdValue : engine2Value,
      afterThreshold:
        engine2Value > thresholdValue ? engine2Value - thresholdValue : 0,
      underThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : bvrColour.codes["200"],
      overThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : primaryEngine === 2
          ? bvrColour.codes["200"]
          : bvrColour.codes["900"],
    },
  ];

  const domainMin = 0;

  const margin = 0.1 * maxEngineValue;
  const domainMax =
    maxEngineValue < thresholdValue
      ? thresholdValue + margin
      : Math.ceil((maxEngineValue + margin) / 10) * 10;
  const domain = [domainMin, domainMax];

  const mobileMargin = 0.04 * maxEngineValue;
  const mobileDomainMax =
    maxEngineValue < thresholdValue
      ? thresholdValue + mobileMargin
      : Math.ceil(maxEngineValue + mobileMargin);
  const mobileDomain = [domainMin, mobileDomainMax];

  const thresholdLineColour = bvrColour.codes["900"];

  return (
    <BehaviourBarGraph
      data={data}
      threshold={thresholdValue}
      thresholdLabel={`Signol threshold${
        !isExample
          ? ` (${formatPercentage("0-100", thresholdPercentage * 100).formattedValue})`
          : ""
      }`}
      mobileDomain={mobileDomain}
      domain={domain}
      refLineColour={thresholdLineColour}
      success={successStatus}
      unitOfMeasure={unit}
      axisLabel={`Fuel consumption after cool down time`}
      hideYLine={true}
      hideNum={true}
      barLabelFormatter={(value) =>
        `${formatMass(industry, value, 0, { boundaryValues: [0, thresholdValue] }).formattedValue}`
      }
      isExample={isExample}
    />
  );
}
